import axios from "axios";
import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { courtAdd } from "../../../api/endpoint";
import toast from "react-hot-toast";
import Select from "react-select";
import { useSelector } from "react-redux";
import Input from "../../../components/input";
import Loader from "../../../components/loader";

const EditCourtModal = ({ isEditOpen, closeEditModal, selectedCourt, fetchCourts }) => {
  const initialCourtState = {
    courtName: "",
    address: "",
    zipcode: "",
    sports: [],
    latitude: "",
    longitude: "",
    environment: [],
    access: "true",
    phone: "",
    surfaceQuality: "",
    courtSize: "",
    numberOfIndoorCourt: "",
    numberOfOutdoorCourt: "",
    amenities: [],
  };
  const [newCourt, setNewCourt] = useState(initialCourtState);
  const [sportsList, setSportsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    courtName: "",
    address: "",
    zipcode: "",
    sports: "",
    environment: "",
    access: "",
    phone: "",
    surfaceQuality: "",
    courtSize: "",
    numberOfIndoorCourt: "",
    numberOfOutdoorCourt: "",
    amenities: "",
  });
  const { auth } = useSelector((state) => state.login);
  const { courts } = useSelector((state) => state.courts);
  const { sports } = useSelector((state) => state.sports);

  useEffect(() => {
    if (isEditOpen && selectedCourt && courts?.length > 0 && sports.length > 0) {
      prefillData();
    }
  }, [isEditOpen, selectedCourt, courts, sports]);

  useEffect(() => {
    if (!isEditOpen) {
      setNewCourt(initialCourtState);
      setErrors({});
    }
  }, [isEditOpen]);

  useEffect(() => {
    const sportsData = sports?.map((sport) => ({
      value: sport?.id,
      label: sport?.sportName,
    }));
    setSportsList(sportsData);
  }, [sports]);

  const prefillData = () => {
    const courtData = courts?.find((court) => court?.id === selectedCourt);
    if (!courtData) return;
    let environment = [];
    if (courtData?.environment?.length === 2) {
      environment = [
        { value: "OUTDOOR", label: "Outdoor" },
        { value: "INDOOR", label: "Indoor" },
      ];
    } else if (courtData?.environment[0] === "INDOOR") {
      environment = [{ value: "INDOOR", label: "Indoor" }];
    } else if (courtData?.environment[0] === "OUTDOOR") {
      environment = [{ value: "OUTDOOR", label: "Outdoor" }];
    }
    setNewCourt({
      ...courtData,
      sports:
        courtData.sportsId?.map((id, index) => ({
          value: id,
          label: courtData.sportsName?.[index] || "",
        })) || [], // Merging sportsId and sportsName into one array
      amenities: courtData.amenities
        ? courtData.amenities.map((amenity) => ({
            value: amenity,
            label: amenity,
          }))
        : [], // Handle null amenities gracefully
      environment: environment,
    });
  };

  const validateFields = () => {
    const newErrors = {};

    // Validate Court Name (only letters and spaces allowed)
    if (!newCourt.courtName.trim()) {
      newErrors.courtName = "Court name is required.";
    } else if (!/^[a-zA-Z\s]*$/.test(newCourt.courtName)) {
      newErrors.courtName = "Court name should only contain letters.";
    }

    // Validate Address
    if (!newCourt.address.trim()) {
      newErrors.address = "Address is required.";
    }

    // Validate Zipcode (5 digits only)
    if (!newCourt.zipcode) {
      newErrors.zipcode = "Zip code is required.";
    }

    // Validate Sports (at least one sport selected)
    if (!newCourt.sports.length) {
      newErrors.sports = "At least one sport must be selected.";
    }

    // Validate phone number allowing digits, dashes and ensuring 10 or 12 digits
    if (
      newCourt.phone &&
      (!/^[0-9\-]+$/.test(newCourt.phone) || // only digits and dashes
        !/^(\d{10,12})$/.test(newCourt.phone.replace(/-/g, ""))) // must be 10 to 12 digits ignoring dashes
    ) {
      newErrors.phone = "Phone number must be 10 to 12 digits and can include optional dashes.";
    }

    if (newCourt?.numberOfIndoorCourt !== "" && newCourt?.numberOfIndoorCourt < 0) {
      newErrors.numberOfIndoorCourt = "Number of Courts cannot be a negative number";
    }

    if (newCourt?.numberOfOutdoorCourt !== "" && newCourt?.numberOfOutdoorCourt < 0) {
      newErrors.numberOfOutdoorCourt = "Number of Courts cannot be a negative number";
    }

    // Update errors state
    setErrors(newErrors);

    // Return true if no errors, false otherwise
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return; // Stop submission if validation fails

    let courtToSubmit = {
      ...newCourt,
      numberOfIndoorCourt: newCourt?.numberOfIndoorCourt == "" ? null : Number(newCourt?.numberOfIndoorCourt),
      numberOfOutdoorCourt: newCourt?.numberOfOutdoorCourt == "" ? null : Number(newCourt?.numberOfOutdoorCourt),
      sports: newCourt.sports.map((sport) => sport.value), // Sending only sports as an array of IDs
      amenities: newCourt.amenities?.map((amenity) => amenity.value) || [],
      environment: newCourt?.environment?.length == 2 ? "BOTH" : newCourt?.environment[0]?.value,
    };

    // Remove sportsId and sportsName from the payload
    delete courtToSubmit?.sportsId;
    delete courtToSubmit?.sportsName;

    try {
      setLoading(true);
      const response = await axios.post(courtAdd, [courtToSubmit], {
        params: { id: selectedCourt },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth}`,
        },
      });
      toast.success("Court submitted successfully!");
      fetchCourts();
      handleClose();
    } catch (error) {
      const errorMessage = error.response?.data?.user_description || "An unexpected error occurred.";
      toast.error(errorMessage);
      console.error("Error submitting court:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewCourt(initialCourtState);
    setErrors({
      courtName: "",
      address: "",
      zipcode: "",
      sports: "",
      environment: "",
      access: "",
      phone: "",
      surfaceQuality: "",
      courtSize: "",
      numberOfIndoorCourt: "",
      numberOfOutdoorCourt: "",
      amenities: "",
    });
    closeEditModal();
  };

  return (
    <>
      <Modal show={isEditOpen} onHide={handleClose} size="lg">
        <div className="modal-header">
          <h5 className="modal-title">Update Court</h5>
          <button type="button" className="btn-close" onClick={handleClose}></button>
        </div>
        <div className="modal-body">
          <form>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Input
                  label="Court Name"
                  type="text"
                  id="courtName"
                  placeholder="Court Name"
                  value={newCourt?.courtName || ""}
                  onChange={(e) => setNewCourt({ ...newCourt, courtName: e.target.value })}
                />
                {errors?.courtName && <div className="text-danger">{errors?.courtName}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <Input
                  label="Address"
                  type="textarea"
                  id="address"
                  placeholder="Address"
                  value={newCourt?.address || ""}
                  onChange={(e) => setNewCourt({ ...newCourt, address: e.target.value })}
                />
                {errors?.address && <div className="text-danger">{errors?.address}</div>}
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="sports" className="form-label">
                  Sport
                </label>
                <Select
                  id="sports"
                  value={newCourt?.sports || []}
                  onChange={(selectedOptions) => setNewCourt({ ...newCourt, sports: selectedOptions })}
                  options={sportsList}
                  isMulti={true}
                  closeMenuOnSelect={false}
                />
                {errors?.sports && <div className="text-danger">{errors?.sports}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="access" className="form-label">
                  Access
                </label>
                <select
                  className="form-control"
                  id="access"
                  value={newCourt?.access || ""}
                  onChange={(e) => setNewCourt({ ...newCourt, access: e.target.value })}
                >
                  <option value={true}>Public</option>
                  <option value={false}>Private</option>
                </select>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Input
                  label="Number of Indoor Courts"
                  type="text"
                  placeholder="Number of Indoor Courts"
                  id="numberOfIndoorCourt"
                  value={newCourt?.numberOfIndoorCourt || ""}
                  onChange={(e) =>
                    setNewCourt({
                      ...newCourt,
                      numberOfIndoorCourt: e.target.value,
                    })
                  }
                />
                {errors?.numberOfIndoorCourt && <div className="text-danger">{errors?.numberOfIndoorCourt}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <Input
                  label="Number of Outdoor Courts"
                  type="text"
                  placeholder="Number of Outdoor Courts"
                  id="numberOfOutdoorCourt"
                  value={newCourt?.numberOfOutdoorCourt || ""}
                  onChange={(e) =>
                    setNewCourt({
                      ...newCourt,
                      numberOfOutdoorCourt: e.target.value,
                    })
                  }
                />
                {errors?.numberOfOutdoorCourt && <div className="text-danger">{errors?.numberOfOutdoorCourt}</div>}
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="amenities" className="form-label">
                  Environment
                </label>
                <Select
                  isMulti
                  value={newCourt.environment || []}
                  onChange={(selectedOptions) => setNewCourt({ ...newCourt, environment: selectedOptions })}
                  options={[
                    { value: "INDOOR", label: "Indoor" },
                    { value: "OUTDOOR", label: "Outdoor" },
                  ]}
                />
              </div>
              <div className="col-md-6 mb-3">
                <Input
                  label="Surface Quality"
                  type="text"
                  id="surfaceQuality"
                  placeholder="Surface Quality"
                  value={newCourt?.surfaceQuality || ""}
                  onChange={(e) => setNewCourt({ ...newCourt, surfaceQuality: e.target.value })}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Input
                  label="Court Size"
                  type="text"
                  id="courtSize"
                  placeholder="Court Size"
                  value={newCourt?.courtSize || ""}
                  onChange={(e) => setNewCourt({ ...newCourt, courtSize: e.target.value })}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="amenities">Amenities</label>
                <Select
                  id="amenities"
                  isMulti
                  value={newCourt.amenities || []}
                  onChange={(selectedOptions) => setNewCourt({ ...newCourt, amenities: selectedOptions })}
                  options={[{ value: "Lighted Courts", label: "Lighted Courts" }]}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Input
                  label="Phone"
                  type="text"
                  id="phone"
                  placeholder="Phone"
                  value={newCourt?.phone || ""}
                  onChange={(e) => setNewCourt({ ...newCourt, phone: e.target.value })}
                />
                {errors?.phone && <div className="text-danger">{errors?.phone}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <Input
                  label="Zip Code"
                  type="text"
                  className="form-control"
                  id="zipcode"
                  placeholder="Zip Code"
                  value={newCourt?.zipcode || ""}
                  onChange={(e) => setNewCourt({ ...newCourt, zipcode: e.target.value })}
                />
                {errors?.zipcode && <div className="text-danger">{errors?.zipcode}</div>}
              </div>
            </div>
          </form>
        </div>
        <div className="modal-footer">
          <div className="d-flex justify-content-end">
            <button
              onClick={(e) => handleSubmit(e)}
              className="btn"
              style={{ backgroundColor: "#2c3e50", color: "#ecf0f1" }}
            >
              Update Court
            </button>
          </div>
        </div>
      </Modal>
      {loading && <Loader />}
    </>
  );
};

export default EditCourtModal;
